import {
    Button,
    ButtonProps,
    Card,
    Collapsible,
    Divider,
    Headline,
    Icon,
    IconNameType,
    Spinner,
    Stack,
    Tag,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, ReactNode, SyntheticEvent, useState } from 'react';
import { usePortalMode } from '../../hooks/use-portal-mode';
import { ContentButton } from '../link';

interface PageCollapsibleBlockPropsStrict {
    allRecordsHref: string;
    children: ReactNode;
    className?: string;
    count: number;
    loading: boolean;
    onAllRecordsClick?: (e?: SyntheticEvent<HTMLElement>) => void;
    title: string;
    icon?: IconNameType;
}

export interface PageCollapsibleBlockProps extends PageCollapsibleBlockPropsStrict {
    /** Unstrict Props */
    [propName: string]: any;
}

export const PageCollapsibleBlock: FC<PageCollapsibleBlockProps> = observer(props => {
    const {
        allRecordsHref,
        children,
        className,
        count,
        icon,
        loading,
        onAllRecordsClick,
        title,
        ...otherProps
    } = props;

    const mode = usePortalMode();

    const [opened, setOpened] = useState(true);

    const ButtonEl = onAllRecordsClick ? Button : (ContentButton as FC<ButtonProps>);

    return (
        <Card padding="none" className={className} {...otherProps}>
            <Stack
                className="w-100 p-3 box-sizing-border-box"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack alignItems="center" spacing={1}>
                    {!!icon && <Icon name={icon} size={24} />}
                    <Headline size="small" className="m-0">
                        {title}
                    </Headline>
                    <Tag badge compact data-cy="collapsible-block-count-tag">
                        {count}
                    </Tag>
                </Stack>
                <Stack spacing={1}>
                    <ButtonEl
                        small
                        primary
                        fill="subtle"
                        href={allRecordsHref}
                        onClick={onAllRecordsClick}
                    >
                        View All
                    </ButtonEl>
                    <Button
                        iconName={opened ? 'expand_less' : 'expand_more'}
                        small
                        fill="subtle"
                        onClick={() => setOpened(!opened)}
                        aria-label={opened ? 'Collapse' : 'Expand'}
                    />
                </Stack>
            </Stack>

            <Collapsible animate open={opened}>
                <Divider />
                <div className={classNames({ 'p-3': !mode.mobile, 'p-2': mode.mobile })}>
                    {loading ? (
                        <Stack justifyContent="center">
                            <Spinner size="small" />
                        </Stack>
                    ) : (
                        children
                    )}
                </div>
            </Collapsible>
        </Card>
    );
});
